if (typeof window.stockmanBundle === "undefined") {
  window.stockmanBundle = () => {
    const MODULE_NAME = 'Stockman Bundle';
    const MINIMUM_AMOUNT = 16495;
    const DEFAULT_LOCAL_STORAGE_KEY = 'build-a-box-items';
    const LOCAL_STORAGE_AUTO_APPEND_ID_KEY = 'build-a-box-auto-append';
    const LOCAL_STORAGE_AUTO_APPEND_QTY_KEY = 'build-a-box-auto-append-qty';
    const isCartPage = window.location.pathname.includes('/cart');
    const TOOLTIP_HIDE_TIMEOUT = {};

    // --- FEATURE FLAGS ---
    const FEATURE_EXCLUDE_PREORDER = true; // Toggle on (true) or off (false)

    let $root = null;
    let mode = 'ADD'; // available mode: ADD | EDIT;
    let lineItemKey = null;

    // This configuration will be different depending on the product
    const configuration = {
      bundle_list: null,
      bundle_data: null,
    };

    // Helper to allow dynamic liquid config override, falling back to local flag
    const shouldExcludePreorder = () => {
      if (typeof configuration.bundle_data?.exclude_preorder === 'boolean') {
        return configuration.bundle_data.exclude_preorder;
      }
      return FEATURE_EXCLUDE_PREORDER;
    };

    // Data that will be constantly updated depending on the original configuration
    const data = {
      qtyMap: {},
      productMapByVariantId: {},
      productMapByTitle: {},
      unitPriceMap: {},
      sellingPlanMap: {},
    };

    let lastCountValue = null;
    let countAnimationTimeout = null;

    const setMode = (value) => {
      mode = value?.toUpperCase() === 'EDIT' ? 'EDIT' : 'ADD';
    }

    const printLog = (message) => {
      console.log(
        `%c${MODULE_NAME}`, // Module Name
        'font-weight: bold; color: #000; background:rgb(206, 236, 148); border-radius: 3px; padding-left: 5px; padding-right: 5px;',
        message,
      );
    }

    const debounce = (func, wait) => {
      let timeout;
      return function(...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
      };
    };
    
    const getCurrentQty = (variantId) => {
      return data.qtyMap[variantId] ?? 0;
    }

    const getProduct = (variantId) => {
      return data.productMapByVariantId[variantId] ?? null;
    }
    
    const getProductByTitle = (title) => {
      return data.productMapByTitle[title] ?? null;
    }

    const getProductId = (variantId) => {
      return getProduct(variantId)?.id ?? 0;
    }
    
    const getLinkedCollectionId = (variantId) => {
      return getProduct(variantId)?.linkedCollectionId ?? 0;
    }

    const getUnitPrice = (variantId) => {
      return getProduct(variantId)?.unitPrice ?? 0;
    }

    const getSellingPlanId = (variantId, numberOfWeeks) => {
      if (!variantId || !numberOfWeeks) {
        return null;
      }

      return data.sellingPlanMap[variantId]?.[numberOfWeeks] || null;
    }

    const getParentSellingPlanId = (numberOfWeeks) => {
      if (typeof(configuration.bundle_data.bundle_parent_selling_plans) === 'undefined') {
        return null;
      }

      const matchingSellingPlan = configuration.bundle_data.bundle_parent_selling_plans.find(sellingPlan => {
        const sellingPlanType = sellingPlan.name;
        const sellingPlanId = sellingPlan.id;
        
        const numberMatch = sellingPlanType.match(/\d+/);
        const sellingPlanNumberOfWeeks = numberMatch ? Number(numberMatch[0]) : null;

        return sellingPlanNumberOfWeeks == numberOfWeeks
      });

      if (!matchingSellingPlan) {
        return null;
      }

      return matchingSellingPlan.id;
    }

    const getTotalAmount = () => {
      const excludePreorder = shouldExcludePreorder();

      let total = 0;

      Object.entries(data.qtyMap).forEach(([variantId, qty]) => {
        const unitPrice = getUnitPrice(variantId);
        const product = getProduct(variantId);

        if (!product) {
          return;
        }
       
        const isPreOrder = product.tags?.includes('preorder') || false;
       
        if (excludePreorder && isPreOrder) {
          return;
        }

        total += unitPrice * qty;
      });

      return Number(total);
    }

    const getTotalCount = () => {
      return Object.values(data.qtyMap).reduce((acc, item) => {
        acc += item;

        return acc;
      }, 0);
    }

    const getDeliveryFrequency = () => {
      return $('[name=delivery_frequency]:checked')?.val() || null;
    }

    const getCurrentCartItems = () => {
      const list = [];

      Object.entries(data.qtyMap).forEach(([variantId, qty]) => {
        const product = getProduct(variantId);

        if (product && qty > 0) {
          list.push({
            product,
            qty,
          })
        }
      });

      return list;
    }

    const getBundleElements = () => {
      return $root ? $root.find('.ss-bundle') : $('.ss-bundle');
    }

    const isAllCartItemsPreOrder = () => {
      const cartItems = getCurrentCartItems();

      if (!cartItems || !cartItems.length) {
        return false;
      }

      return cartItems.every(({ product }) => product.tags?.includes('preorder') || false);
    }

    const validateData = () => {
      Object.entries(data.qtyMap).forEach(([variantId, qty]) => {
        const product = getProduct(variantId);

        if (!product || !product.available) {
          delete data.qtyMap[variantId];
        }
      });
    }

    const plusQty = (variantId) => {
      changeQty(variantId, getCurrentQty(variantId) + 1);
    }

    const minusQty = (variantId) => {
      const currentQty = data.qtyMap[variantId] ?? 0;
      changeQty(variantId, getCurrentQty(variantId) - 1);
    }

    const changeQty = (variantId, qty) => {
      if (qty < 0) {
        qty = 0;
      }

      data.qtyMap[variantId] = qty;
    }

    const validate = () => {
      if (configuration.bundle_data.bundle_type === 'fixed') {
        return validateFixedBundles();
      }

      return validateDynamicBundles();
    }

    /**
     * Validate the fixed bundles
     * Target: Custom Box & Value Box
     * @return {boolean}
     */
    const validateFixedBundles = () => {
      const $bundles = getBundleElements();

      if (!$bundles.length > 0) {
        return false;
      }

      // Populate the hashmap
      const bundleRules = {};
      $bundles.each(function() {
        const collectionId = $(this).data('collection-id');
        const requiredAmount = $(this).data('required-amount');

        bundleRules[collectionId] = {
          current: 0,
          max: Number(requiredAmount),
        }
      })

      // Go through the cart and reduce the quantity until everything is 0
      const cartItems = getCurrentCartItems();

      cartItems.forEach(({ product, qty }) => {
        const { linkedCollectionId } = product;

        if (typeof(bundleRules[linkedCollectionId]) !== 'undefined') {
          bundleRules[linkedCollectionId].current += qty;
        }
      })

      // Disable / Enable other "Add to Box"
      Object.entries(bundleRules).forEach(([linkedCollectionId, bundleRule]) => {
        const { current, max } = bundleRule;
        const percentage = current === 0 ? 0 : current / max * 100;
        const $bundle = $(`[data-collection-id="${linkedCollectionId}"]`);
        const $tick = $bundle.closest('.ss-bundle-group').find('.ss-tick-progress');
        const $plusButtons = $(`[data-ss-bundle-trigger="qty-plus"][data-linked-collection-id="${linkedCollectionId}"]`);

        if (percentage < 100) {
          $plusButtons.removeAttr('disabled', 'disabled');
        } else {
          $plusButtons.attr('disabled', 'disabled');
        }
        
        // Update the tick progress bar
        window.stockmansteaks.updateTickProgress($tick.get(0), percentage);
      });

      // Check if all the value is 0
      return Object.values(bundleRules).every(bundleRule => bundleRule.current === bundleRule.max);
    }

    /**
     * Get validation message for the submit button tooltip
     * @return {string} - Validation message or empty string if valid
     */
    const getValidationMessage = () => {
      const bundleType = configuration.bundle_data.bundle_type;
      
      if (bundleType === 'dynamic') {
        // Dynamic bundle validation (Build a Box)
        const totalAmount = getTotalAmount();
        const isAllPreOrders = isAllCartItemsPreOrder();

        if (totalAmount === 0) {
          return 'Please add items to your box';
        }
        
        if (isAllPreOrders) {
          return ''; // Pre-order only items don't have minimum requirements
        }
        
        if (totalAmount < MINIMUM_AMOUNT) {
          const remainingAmount = MINIMUM_AMOUNT - totalAmount;
          return `You need to add $${(remainingAmount / 100).toFixed(2)} to your box`;
        }
        
        return 'Add box to your cart'; // Valid
      } else if (bundleType === 'fixed') {
        // Fixed bundle validation (Custom Box, Value Box)
        const $bundles = getBundleElements();
        
        if (!$bundles.length > 0) {
          return '';
        }
        
        // Populate the hashmap
        const bundleRules = {};
        $bundles.each(function() {
          const $bundle = $(this);
          const collectionId = $bundle.data('collection-id');
          const collectionName = $bundle.data('collection-name') || 'Menu';
          const requiredAmount = $bundle.data('required-amount');
          
          bundleRules[collectionId] = {
            current: 0,
            max: Number(requiredAmount),
            name: collectionName,
          };
        });
        
        // Go through the cart and count items per collection
        const cartItems = getCurrentCartItems();
        cartItems.forEach(({ product, qty }) => {
          const { linkedCollectionId } = product;
          
          if (typeof(bundleRules[linkedCollectionId]) !== 'undefined') {
            bundleRules[linkedCollectionId].current += qty;
          }
        });
        
        // Build the validation message for incomplete collections
        const incompleteCollections = [];
        Object.values(bundleRules).forEach(bundleRule => {
          const { current, max, name } = bundleRule;
          const remaining = max - current;
          
          if (remaining > 0) {
            incompleteCollections.push(`${remaining} item${remaining !== 1 ? 's' : ''} to ${name}`);
          }
        });
        
        if (incompleteCollections.length > 0) {
          return `Please add ${incompleteCollections.join(' & ')}`;
        }
        
        return 'Add box to your cart'; // Valid
      }
      
      return '';
    }

    /**
     * Validate the dynamic bundles
     * Target: Build a Box
     * @return {boolean}
     */
    const validateDynamicBundles = () => {
      const totalAmount = getTotalAmount();
      const isAllPreOrders = isAllCartItemsPreOrder();
      
      // We are assuming that we only have 1 tick
      const $tick = $('.ss-tick-progress');

      // Pre-order Only calculation
      if (isAllPreOrders) {
        window.stockmansteaks.updateTickProgress($tick.get(0), 100);
        return true;
      }
      
      // Default calculation
      const percentage = totalAmount === 0 ? 0 : Math.min(100, totalAmount / MINIMUM_AMOUNT * 100);
      
      // Update the tick progress bar
      window.stockmansteaks.updateTickProgress($tick.get(0), percentage);

      if (totalAmount < MINIMUM_AMOUNT) {
        return false;
      }

      return true;
    }

    const applySearch = ($el) => {
      const $wrapper = $el.closest('.ss-bundle-wrapper');
      const $input = $wrapper.find('[data-ss-bundle-trigger="search-input"]');
      const keyword = $input.val();
      const lowercaseKeyword = keyword?.toLowerCase().trim() || '';
      const $clear = $wrapper.find('[data-ss-bundle-trigger="search-clear"]');

      recalculateListing($wrapper);

      // Show / Hide Clear Button
      if (lowercaseKeyword) {
        $clear.fadeIn('fast');
      } else {
        $clear.fadeOut('fast');
      }
    }
    
    // Create the debounced function once
    const debouncedSearch = debounce(($el) => {
      applySearch($el);
    }, 250);

    const clearSearch = ($el) => {
      const $wrapper = $el.closest('.ss-bundle-wrapper');
      const $input = $wrapper.find('[data-ss-bundle-trigger="search-input"]');

      $input.val('');

      applySearch($input);
    }

    const applyFilter = ($el) => {
      const $wrapper = $el.closest('.ss-bundle-wrapper');
      const $filter = $el;
      const isActive = $filter.hasClass('ss-bundle-filter__item--active');

      // Set everything else to inactive
      $('.ss-bundle-filter__item--active').removeClass('ss-bundle-filter__item--active');

      // Set this to active if it wasn't active previously
      if (!isActive) {
        $filter.addClass('ss-bundle-filter__item--active')
      }

      recalculateListing($wrapper);
    }

    const recalculateListing = ($wrapper) => {
      const $input = $wrapper.find('[data-ss-bundle-trigger="search-input"]');
      const keyword = $input.val();
      const lowercaseKeyword = keyword?.toLowerCase().trim() || '';
      const $activeFilters = $wrapper.find('.ss-bundle-filter__item--active');
      const activeFilters = [];

      $activeFilters.each(function() {
        const filterValue = $(this).data('value');
        activeFilters.push(filterValue.toLowerCase());
      })

      // Filter
      $('.ss-bundle__item').each(function() {
        const $item = $(this);
        const title = $item.find('.ss-bundle__product-title').text().trim().toLowerCase();
        const variantId = $item.find('[data-variant-id]').data('variant-id');
        const productFilters = data.productMapByVariantId[variantId]?.tags || [];

        const matchesKeyword = lowercaseKeyword ? title.includes(lowercaseKeyword) : true;
        const matchesFilters = activeFilters.length > 0 ? activeFilters.some(activeFilter => productFilters.some(productFilter => productFilter === activeFilter)) : true;
        
        if (matchesKeyword && matchesFilters) {
          $item.show();
        } else {
          $item.hide();
        }
      });
    }

    const refreshItem = (variantId) => {
      const $input = $(`.ss-bundle__qty-field[data-variant-id="${variantId}"]`);
      const $item = $input.closest('.ss-bundle__item');
      const currentQty = getCurrentQty(variantId);

      if ($input.length) {
        $input.val(getCurrentQty(variantId));
      }

      if ($item.length) {
        if (currentQty > 0) {
          $item.addClass('ss-bundle__item--active');
        } else {
          $item.removeClass('ss-bundle__item--active');
        }
      }
    }

    const refreshDeliveryFrequency = () => {
      const $deliveryFrequency = $('[data-ss-bundle-type="delivery-frequency"]');
      const deliveryFrequency = getDeliveryFrequency();

      if ($deliveryFrequency.length > 0) {
        if (deliveryFrequency) {
          $deliveryFrequency.html(`Delivered every <strong>${deliveryFrequency} weeks</strong>`);
        } else {
          $deliveryFrequency.html('');
        }
      }
    }

    const refreshSummary = () => {
      const $count = $('[data-ss-bundle-type="count"]');
      const $countNumber = $('[data-ss-bundle-type="count-number"]');
      const count = getTotalCount();
      const $totalAmount = $('[data-ss-bundle-type="total"]');
      const totalAmount = getTotalAmount();

      if ($count.length) {
        $count.text(count > 1 ? `${count} items` : `${count} item`);
      }

      if ($countNumber.length) {
        $countNumber.text(count);

        if (lastCountValue !== count) {
          $countNumber.addClass('jump');

          countAnimationTimeout = setTimeout(() => {
            $countNumber.removeClass('jump');
          }, 400);
        }
      }

      if ($totalAmount.length) {
        const printableTotal = (totalAmount / 100).toFixed(2);
        $totalAmount.text(`$${printableTotal}`);
      }

      lastCountValue = count;

      populateCartItems();
    }

    const refreshSubmitButton = () => {
      const $submit = $('[data-ss-bundle-trigger="submit"]');
      const isValid = validate();

      if (!isValid) {
        $submit.attr('disabled', 'disabled');
      } else {
        $submit.removeAttr('disabled');
      }
      
      refreshValidationTooltip();
    }

    const refreshValidationTooltip = () => {
      const $tooltip = $('[data-validation-tooltip]');
      
      if (!$tooltip.length) {
        return;
      }
      
      const validationMessage = getValidationMessage();
      
      $tooltip.text(validationMessage);
    }

    const populateCartItems = () => {
      const cartItems = getCurrentCartItems();
      const $cartBody = $('.ss-bundle-cart');
      const $cartEmpty = $('.ss-bundle-cart__empty');

      const allowedCollectionIds = configuration.bundle_data?.bundle_colletion_ids || [];

      const $bundle = getBundleElements();

      if (cartItems.length > 0) {
        $cartEmpty.hide();
        $cartBody.show();

        $cartBody.empty();

        // Group Cart Items by Collection
        const groupedCartItems = {};

        if ($bundle) {
          $bundle.each(function() {
            const collectionId = $(this).data('collection-id');
            const collectionName = $(this).data('collection-name');

            if (!allowedCollectionIds.includes(collectionId)) {
              return;
            }

            groupedCartItems[collectionId] = {
              id: collectionId,
              name: collectionName || '',
              items: [],
            };
          });
        }

        cartItems.forEach((cartItem) => {
          const product = cartItem.product;
          const linkedCollectionId = product.linkedCollectionId;
          
          if (typeof(groupedCartItems[linkedCollectionId]) === 'undefined') {
            return;
          }

          groupedCartItems[linkedCollectionId].items.push(cartItem);
        });

        // Populate grouped cart items
        Object.values(groupedCartItems).forEach((groupedCartItem) => {
          const { name, items } = groupedCartItem;

          if (name) {
            const $cartItemMenuHeading = $(`
              <div class="ss-bundle-cart__menu-heading">
                ${name}
              </div>
            `);
            
            $cartBody.append($cartItemMenuHeading);
          }

          items.forEach((cartItem) => {
            const product = cartItem.product;
            const linkedCollectionId = product.linkedCollectionId;
            const qty = cartItem.qty;

            const $cartItem = $(`
              <div class="ss-bundle-cart__item">
                <div class="ss-bundle-cart__qty">
                  <div class="ss-bundle__qty-wrapper">
                    <button
                      type="button"
                      class="ss-bundle__button ss-bundle__qty-button ss-bundle__qty-button--small"
                      data-ss-bundle-trigger="qty-minus"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                        data-slot="icon"
                        class="h-5 w-5 md:h-6 md:w-6"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"></path>
                      </svg>
                    </button>
                    <input
                      type="text"
                      name="ss-bundle-qty-{{ product.id }}"
                      value="${qty}"
                      class="ss-bundle__qty-field"
                      data-variant-id="${product.variantId}"
                      readonly
                    >
                    <button
                      type="button"
                      class="ss-bundle__button ss-bundle__qty-button ss-bundle__qty-button--small"
                      data-ss-bundle-trigger="qty-plus"
                      data-linked-collection-id="${linkedCollectionId}"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                        data-slot="icon"
                        class="h-5 w-5 md:h-6 md:w-6"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="ss-bundle-cart__title">
                  ${product.title}
                </div>
                <button class="ss-bundle-cart__remove" data-ss-bundle-trigger="qty-set" data-value="0" data-variant-id="${product.variantId}" title="Remove" aria-label="Remove">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation" class="icon icon-remove">
                    <path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"></path>
                    <path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5zM9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z" fill="currentColor"></path>
                  </svg>
                </button>
              </div>
            `);

            $cartBody.append($cartItem);
          });
        });
      } else {
        $cartEmpty.show();
        $cartBody.hide();
      }
    }

    const generateBundleId = () => {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let randomStr = "";

      for (let i = 0; i < 9; i++) {
        randomStr += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      const randomNum = Math.floor(Math.random() * 9_000_000_000_000) + 1_000_000_000_000;

      return `${randomStr}:${randomNum}`;
    }

    const generateDynamicBundlePayload = async (type) => {
      const isSubscription = configuration.bundle_data.is_subscription || false;
      const deliveryFrequency = getDeliveryFrequency();

      if (isSubscription && !deliveryFrequency) {
        throw new Error('Invalid Delivery Frequency');
      }

      if (type === 'recharge') {
        const rechargePayload = {
          externalProductId: configuration.bundle_data.bundle_parent_product_id,
          externalVariantId: configuration.bundle_data.bundle_parent_variant_id,
          selections: [],
        };

        Object.entries(data.qtyMap).forEach(([variantId, qty]) => {
          if (!qty) return;

          const linkedCollectionId = getLinkedCollectionId(variantId);

          rechargePayload.selections.push({
            collectionId: linkedCollectionId,
            externalProductId: getProductId(variantId),
            externalVariantId: variantId,
            quantity: qty,
            sellingPlan: isSubscription ? getSellingPlanId(variantId, deliveryFrequency) : undefined,
          })
        });

        return await recharge.bundle.getDynamicBundleItems(rechargePayload, configuration.bundle_data.bundle_parent_handle);

      } else {
        const newBundleId = generateBundleId();
        const payloadList = [];

        Object.entries(data.qtyMap).forEach(([variantId, qty]) => {
          if (!qty) return;

          const linkedCollectionId = getLinkedCollectionId(variantId);
          
          const payload = {
            id: variantId,
            quantity: qty,
            selling_plan: isSubscription ? getSellingPlanId(variantId, deliveryFrequency) : null,
            properties: {
              _rc_bundle: newBundleId,
              _rc_bundle_parent: configuration.bundle_data.bundle_parent_handle,
              _rc_bundle_variant: configuration.bundle_data.bundle_parent_variant_id,
              _rc_bundle_collection_id: linkedCollectionId,
            },
          };

          payloadList.push(payload);
        });

        return payloadList;
      }
    };

    const generateFixedBundlePayload = async (type) => {
      const isSubscription = configuration.bundle_data.is_subscription || false;
      const deliveryFrequency = getDeliveryFrequency();

      if (isSubscription && !deliveryFrequency) {
        throw new Error('Invalid Delivery Frequency');
      }

      if (type === 'recharge') {
        const rechargePayload = {
          externalProductId: configuration.bundle_data.bundle_parent_product_id,
          externalVariantId: configuration.bundle_data.bundle_parent_variant_id,
          selections: [],
        };

        const contentByName = [];

        Object.entries(data.qtyMap).forEach(([variantId, qty]) => {
          if (!qty) return;

          const linkedCollectionId = getLinkedCollectionId(variantId);
          const product = getProduct(variantId);

          rechargePayload.selections.push({
            collectionId: linkedCollectionId,
            externalProductId: getProductId(variantId),
            externalVariantId: variantId,
            quantity: qty,
          });

          contentByName.push(`${qty}x ${product.title}`);
        });

        const rbId = await recharge.bundle.getBundleId(rechargePayload);
        const stringifiedContent = contentByName.join("\n");

        return [
          {
            id: configuration.bundle_data.bundle_parent_variant_id,
            quantity: 1,
            properties: {
              Contents: stringifiedContent,
              _rb_id: rbId,
            },
            selling_plan: isSubscription ? getParentSellingPlanId(deliveryFrequency) : null,
          }
        ];

      } else {
        throw new Error('generateFixedBundlePayload for non-recharge has not been implemented yet');
      }
    };

    const submit = async ($el) => {
      const $form = $('.ss-bundle-wrapper');
      const $submit = $('[data-ss-bundle-trigger="submit"]');
      const $spinner = $submit.find(".loading-overlay__spinner");
      const ignoreAddonsModal = configuration.bundle_data.bundle_type === 'dynamic' || isCartPage;
      const $deliveryFrequency = $form.find("[name=delivery_frequency]");
      const deliveryFrequency = $deliveryFrequency ? $deliveryFrequency.val() : null;
        
      const toggleFormLoading = (value) => {
        if (value) {
          $submit.addClass("loading");
          $submit.attr("disabled", "disabled");
          $spinner.removeClass("hidden");
        } else {
          $submit.removeClass("loading");
          $submit.removeAttr("disabled");
          $spinner.addClass("hidden");
        }
      };

      toggleFormLoading(true);
      
      let response;

      try {
        switch (mode) {
          case 'ADD': 
            response = await submitAdd(ignoreAddonsModal);
            break;

          case 'EDIT':
            response = await submitEdit();
            break;
        }
      } catch (error) {
        console.error(error);
      }

      if (response === null) {
        toggleFormLoading(false);
        return;
      }
      
      if (!response.ok) {
        toggleFormLoading(false);
        const errorData = await response.json();
        console.error("Cart add failed:", errorData);
        alert(errorData.description || "Something went wrong adding to cart.");
        return;
      }
      
      const responseData = await response.json();

      window.stockmansteaks.replaceSections(responseData);
      window.stockmansteaks.toggleCartEmpty(responseData.item_count === 0);
      
      if (!isCartPage) {
        clearLocalStorage();
      }
      reset();

      if (ignoreAddonsModal) {
        window.location.href = `${window.Shopify.routes.root}cart`;
      }

      toggleFormLoading(false);
    };

    const submitAdd = async (ignoreAddonsModal) => {
      const payload = (configuration.bundle_data.bundle_type === 'fixed') 
        ? await generateFixedBundlePayload('recharge') 
        : await generateDynamicBundlePayload('recharge');
        
      if (payload.length > 0) {
        let url = window.routes.cart_add_url;
        if (ignoreAddonsModal) {
          url += "?ignore-addons-modal=1";
        }

        return await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: payload,
            sections: "cart-live-region-text,cart-icon-bubble,main-cart-items,main-cart-footer",
          }),
        });
      }

      return null;
    };

    const submitEdit = async () => {
      const payload = (configuration.bundle_data.bundle_type === 'fixed') 
        ? await generateFixedBundlePayload('recharge') 
        : await generateDynamicBundlePayload('recharge');
        
      if (payload.length > 0) {
        let url = window.routes.cart_change_url;

        return await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            id: lineItemKey,
            properties: payload[0].properties,
            sections: "cart-live-region-text,cart-icon-bubble,main-cart-items,main-cart-footer",
          }),
        });
      }

      return null;
    };

    const autoAddItems = () => {
      let variantIds = [];

      const localStorageIdData = window.stockmansteaks.loadLocalStorage(LOCAL_STORAGE_AUTO_APPEND_ID_KEY);
      const localStorageQtyData = window.stockmansteaks.loadLocalStorage(LOCAL_STORAGE_AUTO_APPEND_QTY_KEY) || 1;
      if (localStorageIdData) {
        for (let i = 0; i < localStorageQtyData; i++) {
          variantIds.push(Number(localStorageIdData));
        }

        window.stockmansteaks.clearLocalStorage(LOCAL_STORAGE_AUTO_APPEND_ID_KEY);
        window.stockmansteaks.clearLocalStorage(LOCAL_STORAGE_AUTO_APPEND_QTY_KEY);
      }

      const url = new URL(window.location.href);
      const addValue = url.searchParams.get("add");
      
      if (addValue) {
        variantIds.push(Number(addValue));
        url.searchParams.delete("add");
        window.history.replaceState({}, document.title, url.toString());
      }

      if (variantIds.length > 0) {
        variantIds.forEach((variantId) => plusQty(variantId));
        showBoxTooltip(`${variantIds.length} item(s) have been added to box`);
      }

      if (isCartPage) {
        saveToLocalStorage();
      }
    }

    const getLocalStorageKey = () => {
      return configuration.bundle_data?.local_storage_key || DEFAULT_LOCAL_STORAGE_KEY;
    }

    const resetSubmitButton = () => {
      const $submit = $('[data-ss-bundle-trigger="submit"]');
      $submit.attr('disabled', 'disabled');
    }
    
    const reset = () => {
      data.qtyMap = {};
      $(`.ss-bundle__qty-field`)?.val(0);
      $('.ss-bundle__item--active')?.removeClass('ss-bundle__item--active');
      
      refreshSummary();
      resetSubmitButton();
    };

    const extractNumber = (text = "") => {
      return parseInt(text.replace(/\D/g, ""));
    };

    const breakRechargeContentProperty = (item) => {
      const regex = /(\d+x)\s(.+)/;
      const breakdown = item.trim().match(regex);

      if (!breakdown || breakdown.length < 2) {
        return { name: null, qty: null };
      }

      const name = breakdown[2]?.trim();
      const qty = extractNumber(breakdown[1]);

      return { name, qty };
    };

    const loadDataFromPayload = (itemsString) => {
      const newQtyMap = {};
      
      itemsString.split("\n").forEach(itemString => {
        const { name, qty } = breakRechargeContentProperty(itemString);
        if (!name) return;

        const product = getProductByTitle(name);
        if (!product) return;

        newQtyMap[product.variantId] = qty;
      });

      data.qtyMap = newQtyMap;
      onAfterLoadData();
    }

    const loadFromLocalStorage = () => {
      const loadedData = window.stockmansteaks.loadLocalStorage(getLocalStorageKey());

      if (loadedData) {
        data.qtyMap = loadedData;
      }

      autoAddItems();
      onAfterLoadData();
    };

    const onAfterLoadData = () => {
      validateData();

      if (!Object.values(data.qtyMap).length) {
        return;
      }

      Object.entries(data.qtyMap).forEach(([variantId, qty]) => {
        refreshItem(variantId);
      });

      refreshDeliveryFrequency();
      refreshSummary();
      refreshSubmitButton();
    };

    const saveToLocalStorage = () => {
      window.stockmansteaks.saveLocalStorage(getLocalStorageKey(), data.qtyMap);
    };
    
    const clearLocalStorage = () => {
      window.stockmansteaks.clearLocalStorage(getLocalStorageKey());
    };

    const onAfterQtyButtonClick = (variantId) => {
      refreshItem(variantId);
      refreshSummary();
      refreshSubmitButton();

      if (!isCartPage) {
        saveToLocalStorage();
      }
    }

    const triggerAddButton = ($el) => {
      const $item = $el.closest('.ss-bundle__item');
      const $cartItem = $el.closest('.ss-bundle-cart__item');

      let $input = $item.length ? $item.find('.ss-bundle__qty-field') : $cartItem.find('.ss-bundle__qty-field');
      const variantId = $input.data('variant-id');

      plusQty(variantId);
      onAfterQtyButtonClick(variantId);
    }

    const triggerRemoveButton = ($el) => {
      const $item = $el.closest('.ss-bundle__item');
      const $cartItem = $el.closest('.ss-bundle-cart__item');

      let $input = $item.length ? $item.find('.ss-bundle__qty-field') : $cartItem.find('.ss-bundle__qty-field');
      const variantId = $input.data('variant-id');

      minusQty(variantId);
      onAfterQtyButtonClick(variantId);
    }

    const triggerSetQuantity = ($el) => {
      const variantId = $el.data('variant-id');
      changeQty(variantId, 0);
      onAfterQtyButtonClick(variantId);
    }

    const triggerSubmit = ($el) => {
      if (validate()) {
        submit($el);
      }
    }

    const triggerModal = ($el) => {
      const target = $el.data('target');
      const $modal = $(target);

      if (!$modal.length) return;
      window.modalMini.toggleModal($modal, true);
    };

    const triggerEditBoxModal = ($el) => {
      const $cartItem = $el.closest('.cart-item');
      const productHandle = $el.data('product-handle');
      const selectedItems = $el.data('items');
      const editType = $el.data('toggle');
      const $modal = $(`#modal-${editType}`);

      if (!$modal.length) throw new Error('Edit modal not found');
      if (!productHandle) throw new Error('Invalid productHandle');

      lineItemKey = $cartItem.length ? $cartItem.data('line-key') : null;

      loadProductConfig(productHandle).then(() => {
        loadDataFromPayload(selectedItems);
      });

      toggleEditBoxModal($modal, true);
    };

    const toggleEditBoxModal = ($modal, value) => {
      const $body = $("body");
      setMode('EDIT');

      if (value) {
        $root = $modal;
        $modal.fadeIn("fast");
        $body.addClass("modal-addons-active");
      } else {
        $root = null;
        $modal.fadeOut("fast");
        $body.removeClass("modal-addons-active");
      }
    };

    const showTooltip = (type) => {
      const $el = $(`.ss-bundle-tooltip[data-type="${type}"]`);
      if (!$el.length || !$el.text().trim()) return;

      $el.fadeIn();
      TOOLTIP_HIDE_TIMEOUT[type] = setTimeout(() => $el.fadeOut(), 5000);
    }

    const showBoxTooltip = (message) => {
      const $tooltip = $('.ss-bundle-tooltip[data-type="box"]');
      if (!$tooltip.length) return;

      $tooltip.text(message);
      showTooltip('box');
    }

    const hideTooltip = () => {
      const $tooltip = $('.ss-bundle-tooltip');
      if ($tooltip.length) $tooltip.fadeOut();
    }
    
    const setupHooks = () => {
      $(document).on('click', '[data-ss-bundle-trigger="qty-plus"]', function(e) {
        e.preventDefault();
        triggerAddButton($(this));
      });
      
      $(document).on('click', '[data-ss-bundle-trigger="qty-minus"]', function(e) {
        e.preventDefault();
        triggerRemoveButton($(this));
      });
      
      $(document).on('click', '[data-ss-bundle-trigger="qty-set"]', function(e) {
        e.preventDefault();
        triggerSetQuantity($(this));
      });

      $(document).on('click', '[data-ss-bundle-trigger="submit"]', function(e) {
        e.preventDefault();
        triggerSubmit($(this));
      });

      $(document).on('click', '[data-ss-bundle-trigger="modal"]', function(e) {
        e.preventDefault();
        triggerModal($(this));
      });

      $(document).on('keyup', '[data-ss-bundle-trigger="search-input"]', function(e) {
        e.preventDefault();
        debouncedSearch($(this));
      });
      
      $(document).on('click', '[data-ss-bundle-trigger="search-clear"]', function(e) {
        e.preventDefault();
        clearSearch($(this));
      });

      $(document).on('click', '[data-ss-bundle-trigger="filter"]', function(e) {
        e.preventDefault();
        applyFilter($(this));
      });
      
      $(document).on('click', '[data-toggle="edit-value-box"]', function(e) {
        e.preventDefault();
        triggerEditBoxModal($(this));
      });

      $(document).on('click', '[data-toggle="edit-custom-box-small"]', function(e) {
        e.preventDefault();
        triggerEditBoxModal($(this));
      });
      
      $(document).on('click', '[data-toggle="edit-custom-box"]', function(e) {
        e.preventDefault();
        triggerEditBoxModal($(this));
      });

      $(document).on('change', '[data-ss-bundle-trigger="delivery-frequency"]', function() {
        refreshDeliveryFrequency();
      });
      
      $(document).on('click', '.ss-bundle-tooltip', function(e) {
        e.preventDefault();
        hideTooltip();
      });
      
      $(document).on('mouseenter', '[data-ss-bundle-trigger="submit"]', function(e) {
        e.preventDefault();
        showTooltip('add-to-cart');
      });

      Fancybox.bind("[data-fancybox]", {});
    };

    const loadProductConfig = async (productHandle = '') => {
      if (!productHandle) throw new Error('productHandle is required');

      reset();
      const url = `${window.location.origin}/products/${productHandle}?view=custom-value-config`;

      const response = await fetch(url, { method: "GET" });
      const configData = await response.json();

      if (!response.ok) {
        console.error("Loading Product Config Failed:", configData);
        alert(configData.description || "Something went wrong when loading the product.");
        return;
      }

      applyProductConfig({
        bundle_list: configData.bundle_list,
        bundle_data: configData.bundle_data,
      });
      
      await setupBasedOnRecharge();
      printLog(`${productHandle} data loaded & applied`);
    }

    const applyProductConfig = (config) => {
      const { bundle_list, bundle_data } = config;

      configuration.bundle_list = bundle_list;
      configuration.bundle_data = bundle_data;

      refreshBundleListPayload();
    }

    const refreshBundleListPayload = () => {
      configuration.bundle_list.forEach((product) => {
        const title = product.title;
        const variantId = product.variantId;
        const sellingPlans = product.sellingPlans;

        data.productMapByVariantId[variantId] = product;
        data.productMapByTitle[title] = product;

        if (sellingPlans && sellingPlans.length > 0) {
          data.sellingPlanMap[variantId] = { 2: null, 4: null, 6: null };

          sellingPlans.forEach(sellingPlan => {
            const sellingPlanType = sellingPlan.name;
            const sellingPlanId = sellingPlan.id;
            
            const numberMatch = sellingPlanType.match(/\d+/);
            const numberOfWeeks = numberMatch ? Number(numberMatch[0]) : null;

            data.sellingPlanMap[variantId][numberOfWeeks] = sellingPlanId;
          });
        }
      });
    }

    const setupProducts = async () => {
      if (typeof(window.ss_bundle_list) === 'undefined') {
        throw new Error('window.ss_bundle_list is not defined');
      }

      if (typeof(window.ss_bundle_data) === 'undefined') {
        throw new Error('window.ss_bundle_data is not defined');
      }

      applyProductConfig({
        bundle_list: window.ss_bundle_list,
        bundle_data: window.ss_bundle_data,
      });
      
      await setupBasedOnRecharge();
    }

    const setupBasedOnRecharge = async () => {
      const rechargeSettings = await window.recharge.cdn.getCDNBundleSettings(configuration.bundle_data.bundle_parent_product_id);

      if (configuration.bundle_data.bundle_type === 'fixed') {
        let maxQuantity;

        if (rechargeSettings?.variants?.[0].items_count) {
          maxQuantity = rechargeSettings?.variants?.[0].items_count;
        }

        if (rechargeSettings?.variants?.[0].option_sources?.length) {
          rechargeSettings.variants[0].option_sources.forEach((option) => {
            const { collection_id, quantity_max } = option;

            if (quantity_max) {
              maxQuantity = quantity_max;
            }

            setupFixedBundleRules(collection_id, maxQuantity);
          }); 
        }
      }
    }

    const setupDynamicBundleRules = () => {
      const $requiredSubtotalText = $('[data-required-subtotal-value]');

      if ($requiredSubtotalText.length > 0) {
        $requiredSubtotalText.text(`$${(MINIMUM_AMOUNT / 100).toFixed(2)}`);
        
        const $bundleDescription = $requiredSubtotalText.closest('p');
        $bundleDescription.slideDown();
      }
    }

    const setupFixedBundleRules = (collectionId, requiredAmount) => {
      const $bundle = $(`[data-collection-id="${collectionId}"]`);
      const $bundleAmountText = $bundle.closest('.ss-bundle-group').find('[data-required-amount-value]');

      if ($bundle.length > 0) {
        $bundle.data('required-amount', requiredAmount);
      }

      if ($bundleAmountText.length > 0) {
        $bundleAmountText.text(requiredAmount);
        
        const $bundleDescription = $bundleAmountText.closest('p');
        $bundleDescription.slideDown();
      }
    }

    const validateRequiredScripts = async (timeout = 5000) => {
      const start = Date.now();

      while (true) {
        const hasJQuery = typeof window.jQuery === "function";
        const hasStockmanSteaks = typeof window.stockmansteaks !== "undefined";
        const hasFancybox = typeof window.Fancybox !== "undefined";

        if (hasJQuery && hasStockmanSteaks && hasFancybox) break;
        if (Date.now() - start >= timeout) break;

        await new Promise(resolve => setTimeout(resolve, 50));
      }
    };

    const init = async () => {
      await validateRequiredScripts();

      if (!isCartPage) {
        await setupProducts();
      }
      
      setupDynamicBundleRules();
      setupHooks();
      
      if (!isCartPage) {
        loadFromLocalStorage();
      }

      printLog("Initialized");
    };

    init();
    printLog("stockman-bundle.js loaded");
  };
}

(function () {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window.stockmanBundle();
    });
  } else {
    window.stockmanBundle();
  }
})();